import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import Navbar from "../../Components/Navbar/Navbar";
import ContactCTA from "../../Components/ContactCTA/ContactCTA";
import Footer from "../../Components/Footer/Footer";

import projects from "../../data/projects";

import { FaArrowRight } from "react-icons/fa";

import "./ProjectDetail.css";

import { FaPlay, FaTimes } from "react-icons/fa";

function ProjectDetail() {
  /*
  ============================================================
  STATE / REFS
  ============================================================
  */

  const videoRef = useRef(null);

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const { slug } = useParams();


  /*
  ============================================================
  FIND PROJECT
  ============================================================
  */

  const projectIndex = projects.findIndex(
    (project) => project.slug === slug
  );

  const project =
    projectIndex !== -1
      ? projects[projectIndex]
      : null;

  const nextProject =
    projectIndex !== -1
      ? projects[
          (projectIndex + 1) % projects.length
        ]
      : null;


  /*
  ============================================================
  OPEN VIDEO
  ============================================================
  */

  const handleOpenVideo = () => {
    if (!project?.video) {
      return;
    }

    setIsVideoOpen(true);
  };


  /*
  ============================================================
  CLOSE VIDEO
  ============================================================
  */

  const handleCloseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();

      videoRef.current.currentTime = 0;
    }

    setIsVideoOpen(false);
  };


  /*
  ============================================================
  CLICK OUTSIDE VIDEO
  ============================================================
  */

  const handleVideoBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseVideo();
    }
  };


  /*
  ============================================================
  ESCAPE KEY
  ============================================================
  */

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isVideoOpen) {
        handleCloseVideo();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isVideoOpen]);


  /*
  ============================================================
  PREVENT BACKGROUND SCROLL
  ============================================================
  */

  useEffect(() => {
    if (isVideoOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isVideoOpen]);


  /*
  ============================================================
  START VIDEO WHEN MODAL OPENS
  ============================================================
  */

  useEffect(() => {
    if (
      isVideoOpen &&
      videoRef.current &&
      project?.video
    ) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.error(
            "Video playback failed:",
            error
          );
        }
      };

      playVideo();
    }
  }, [isVideoOpen, project]);


  /*
  ============================================================
  REDIRECT IF PROJECT DOES NOT EXIST
  ============================================================
  */

  if (!project) {
    return (
      <Navigate
        to="/work"
        replace
      />
    );
  }


  /*
  ============================================================
  PAGE
  ============================================================
  */

  return (
    <>
      <Navbar />

      <main className="project-detail">


        {/* ====================================================
            HERO
        ==================================================== */}

        <section className="project-detail-hero">

          <div className="project-detail-container">


            {/* TOP BAR */}

            <div className="project-detail-topbar">

              <Link
                to="/work"
                className="project-detail-back"
              >

                <span className="project-detail-back-arrow">
                  ←
                </span>

                <span>
                  BACK TO WORK
                </span>

              </Link>


              <span className="project-detail-number">

                PROJECT{" "}

                {String(project.id).padStart(
                  2,
                  "0"
                )}

              </span>

            </div>


            {/* HEADING */}

            <div className="project-detail-heading">

              <div className="project-detail-eyebrow">

                <span>
                  {project.category}
                </span>

                <span className="project-detail-dot" />

                <span>
                  {project.year}
                </span>

              </div>


              <h1>
                {project.title}
              </h1>


              <p className="project-detail-hero-description">

                {project.description}

              </p>

            </div>


            {/* META */}

            <div className="project-detail-meta">


              {/* CLIENT */}

              <div className="project-detail-meta-item">

                <span>
                  CLIENT
                </span>

                <strong>
                  {project.client || "—"}
                </strong>

              </div>


              {/* LOCATION */}

              <div className="project-detail-meta-item">

                <span>
                  LOCATION
                </span>

                <strong>
                  {project.location}
                </strong>

              </div>


              {/* YEAR */}

              <div className="project-detail-meta-item">

                <span>
                  YEAR
                </span>

                <strong>
                  {project.year}
                </strong>

              </div>


              {/* SERVICES */}

              <div className="project-detail-meta-item">

                <span>
                  SERVICES
                </span>

                <strong>
                  {project.services?.join(", ") ||
                    "—"}
                </strong>

              </div>


            </div>

          </div>

        </section>



        {/* ====================================================
            FEATURED MEDIA
        ==================================================== */}

        <section className="project-detail-featured">

          <div className="project-detail-container">

            <figure className="project-detail-featured-media">


              {/* =================================================
                  VIDEO PROJECT
              ================================================= */}

              {project.video ? (

                <div
                  className="project-detail-video-thumbnail"
                  onClick={handleOpenVideo}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {

                    if (
                      e.key === "Enter" ||
                      e.key === " "
                    ) {
                      handleOpenVideo();
                    }

                  }}
                >

                  <img
                    src={
                      project.videoThumbnail ||
                      project.heroImage
                    }
                    alt={`${project.title} video thumbnail`}
                  />


                  {/* DARK OVERLAY */}

                  <div className="project-detail-video-overlay" />


                  {/* PLAY BUTTON */}

                  <button
                    type="button"
                    className="project-detail-play-button"
                    aria-label={`Play ${project.title} video`}
                    onClick={(e) => {

                      e.stopPropagation();

                      handleOpenVideo();

                    }}
                  >

                    <FaPlay />

                  </button>

                </div>

              ) : (

                /* =================================================
                   PHOTOGRAPHY / NON-VIDEO PROJECT
                ================================================= */

                <img
                  className="project-detail-featured-fallback"
                  src={project.heroImage}
                  alt={project.title}
                />

              )}


              {/* CAPTION */}

              <figcaption>

                <span>
                  ETOE PRODUCTIONS
                </span>

                <span>
                  {project.location}
                </span>

              </figcaption>

            </figure>

          </div>

        </section>



        {/* ====================================================
            VIDEO MODAL
        ==================================================== */}

        {isVideoOpen && project.video && (

          <div
            className="project-video-modal"
            onMouseDown={
              handleVideoBackdropClick
            }
          >


            <div className="project-video-modal-content">


              {/* CLOSE BUTTON */}

              <button
                type="button"
                className="project-video-modal-close"
                onClick={handleCloseVideo}
                aria-label="Close video"
              >

                <FaTimes />

              </button>


              {/* VIDEO */}

              <video
                ref={videoRef}
                className="project-video-modal-player"
                src={project.video}
                controls
                autoPlay
                playsInline
              >

                Your browser does not support
                the video tag.

              </video>


              {/* VIDEO INFORMATION */}

              <div className="project-video-modal-info">


                <div>

                  <span className="project-video-modal-category">

                    {project.category}

                  </span>


                  <h3>

                    {project.title}

                  </h3>

                </div>


                <span className="project-video-modal-location">

                  {project.location}

                </span>


              </div>

            </div>

          </div>

        )}



        {/* ====================================================
            PROJECT OVERVIEW
        ==================================================== */}

        <section className="project-detail-overview">

          <div className="project-detail-container">


            {/* SECTION HEADER */}

            <div className="project-detail-section-header">

              <span className="project-detail-section-name">

                PROJECT OVERVIEW

              </span>

            </div>


            {/* CONTENT */}

            <div className="project-detail-overview-content">

              <h2>

                THE STORY

                <span>
                  BEHIND THE WORK
                </span>

              </h2>


              <p>

                {project.description}

              </p>

            </div>

          </div>

        </section>



        {/* ====================================================
            GALLERY
        ==================================================== */}

        {project.gallery?.length > 0 && (

          <section className="project-detail-gallery">

            <div className="project-detail-container">


              {/* GALLERY HEADER */}

              <div className="project-detail-gallery-header">


                <div className="project-detail-section-header">

                  <span className="project-detail-section-name">

                    PROJECT GALLERY

                  </span>

                </div>


                <span className="project-detail-gallery-count">

                  {String(
                    project.gallery.length
                  ).padStart(2, "0")}{" "}

                  VISUALS

                </span>


              </div>



              {/* GALLERY GRID */}

              <div className="project-detail-gallery-grid">

                {project.gallery.map(
                  (image, index) => (

                    <figure
                      className="project-detail-gallery-item"
                      key={`${project.slug}-${index}`}
                    >


                      <div className="project-detail-gallery-image">

                        <img
                          src={image}
                          alt={`${project.title} visual ${
                            index + 1
                          }`}
                        />

                      </div>


                      <figcaption>

                        <span>

                          {String(
                            index + 1
                          ).padStart(
                            2,
                            "0"
                          )}

                        </span>

                        <span>
                          PROJECT VISUAL
                        </span>

                      </figcaption>


                    </figure>

                  )
                )}

              </div>

            </div>

          </section>

        )}



        {/* ====================================================
            NEXT PROJECT
        ==================================================== */}

        <section className="project-detail-next">

          <div className="project-detail-container">


            {/* LABEL */}

            <span className="project-detail-next-label">

              CONTINUE EXPLORING

            </span>


            {/* NEXT PROJECT */}

            {nextProject && (

              <Link
                to={`/work/${nextProject.slug}`}
                className="project-detail-next-card"
              >


                {/* IMAGE */}

                <img
                  src={nextProject.image}
                  alt={nextProject.title}
                />


                {/* OVERLAY */}

                <div className="project-detail-next-overlay" />


                {/* CONTENT */}

                <div className="project-detail-next-content">


                  {/* TOP */}

                  <div className="project-detail-next-top">

                    <span>
                      NEXT PROJECT
                    </span>

                    <span>

                      {nextProject.category}{" "}
                      /{" "}
                      {nextProject.year}

                    </span>

                  </div>


                  {/* BOTTOM */}

                  <div className="project-detail-next-bottom">

                    <h2>
                      {nextProject.title}
                    </h2>


                    <span className="project-detail-next-arrow">

                      <FaArrowRight />

                    </span>

                  </div>


                </div>

              </Link>

            )}

          </div>

        </section>



        {/* ====================================================
            CONTACT
        ==================================================== */}

        <ContactCTA />

      </main>


      <Footer />

    </>
  );
}

export default ProjectDetail;